import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CONSULTATION_TYPES, EMERGENCY_NOTICE } from "@/data/site";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EnquiryForm() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", age: "",
    preferredDate: "", preferredTime: "", service: "", message: "", consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^[+0-9\s\-()]{6,}$/.test(form.phone.trim())) e.phone = "Please enter a valid phone number.";
    if (form.email && !emailRe.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.consent) e.consent = "Please provide your consent to proceed.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error("Please check the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="enquiry-success" className="rounded-3xl border border-gold/30 bg-cream p-10 text-center shadow-card">
        <CheckCircle2 className="mx-auto h-14 w-14 text-terracotta" />
        <h3 className="mt-6 font-serif text-2xl text-charcoal md:text-3xl">Thank you</h3>
        <p className="mx-auto mt-4 max-w-md text-taupe leading-relaxed">
          Thank you for contacting Dr. Mithee's Clinic for Women. Your enquiry has been received. Our clinic team will contact you shortly.
        </p>
        <button
          data-testid="enquiry-new-btn"
          onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", age: "", preferredDate: "", preferredTime: "", service: "", message: "", consent: false }); }}
          className="mt-8 rounded-full border border-terracotta px-6 py-3 font-sans text-sm font-semibold text-terracotta transition-colors hover:bg-blush"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-xl border border-soft-border bg-white p-4 font-sans text-sm text-charcoal outline-none transition-[border-color,box-shadow] placeholder:text-taupe/60 focus:border-terracotta focus:ring-2 focus:ring-terracotta/30";
  const labelCls = "mb-2 block font-sans text-sm font-medium text-charcoal";

  return (
    <form data-testid="enquiry-form" onSubmit={submit} noValidate className="rounded-3xl border border-soft-border bg-cream p-6 shadow-card md:p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className={labelCls}>Full Name *</label>
          <input id="name" data-testid="enquiry-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Your full name" />
          {errors.name && <p className="mt-1.5 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="phone" className={labelCls}>Phone Number *</label>
          <input id="phone" type="tel" data-testid="enquiry-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="+91 XXXXX XXXXX" />
          {errors.phone && <p className="mt-1.5 text-xs text-destructive">{errors.phone}</p>}
        </div>
        <div>
          <label htmlFor="email" className={labelCls}>Email Address</label>
          <input id="email" type="email" data-testid="enquiry-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="you@example.com" />
          {errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="age" className={labelCls}>Age</label>
          <input id="age" data-testid="enquiry-age" value={form.age} onChange={(e) => set("age", e.target.value)} className={inputCls} placeholder="Optional" />
        </div>
        <div>
          <label htmlFor="pdate" className={labelCls}>Preferred Date</label>
          <input id="pdate" type="date" data-testid="enquiry-date" value={form.preferredDate} onChange={(e) => set("preferredDate", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label htmlFor="ptime" className={labelCls}>Preferred Time</label>
          <input id="ptime" type="time" data-testid="enquiry-time" value={form.preferredTime} onChange={(e) => set("preferredTime", e.target.value)} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="service" className={labelCls}>Service / Reason for Consultation</label>
          <select id="service" data-testid="enquiry-service" value={form.service} onChange={(e) => set("service", e.target.value)} className={inputCls}>
            <option value="">Select a reason (optional)</option>
            {CONSULTATION_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="message" className={labelCls}>Message</label>
          <textarea id="message" rows={4} data-testid="enquiry-message" value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} placeholder="Tell us briefly how we can help (optional)" />
        </div>
      </div>

      <label className="mt-5 flex cursor-pointer items-start gap-3">
        <input type="checkbox" data-testid="enquiry-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 h-5 w-5 shrink-0 rounded border-soft-border text-terracotta accent-terracotta" />
        <span className="font-sans text-sm leading-relaxed text-taupe">
          I consent to the clinic contacting me regarding my enquiry or appointment request. *
        </span>
      </label>
      {errors.consent && <p className="mt-1.5 text-xs text-destructive">{errors.consent}</p>}

      <div className="mt-5 flex items-start gap-3 rounded-xl bg-blush p-4">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" />
        <p className="font-sans text-xs leading-relaxed text-taupe">
          This form is for appointment/enquiry purposes and is not intended for medical emergencies. {EMERGENCY_NOTICE}
        </p>
      </div>

      <button
        type="submit"
        data-testid="enquiry-submit"
        disabled={submitting}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 font-sans text-sm font-semibold text-cream shadow-soft transition-[transform,background-color] duration-300 hover:bg-terracotta-dark active:scale-95 disabled:opacity-70 sm:w-auto"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitting ? "Sending…" : "Send Enquiry"}
      </button>
    </form>
  );
}
